import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useCookie } from 'react-use';
import {
  cssColorVar,
  Popover,
  spacer20,
  spacer40,
  spacer8,
  spacer80,
} from '@spotify-internal/encore-web';
import { GridLayout } from '@spotify-internal/encore-web/content-platform/components/GridLayout';
import { useT } from '@spotify-internal/i18n-core';
import { useCurrentUserInfo } from '@hooks/useCurrentUserInfo';
import { useReleaseViewHistory } from '@hooks/useReleaseViewHistory';
import { useSearchedAlbums } from '@hooks/useSearchedAlbums';
import { ContentArea } from '@components/Catalog/ContentArea';
import { SearchModule } from '@components/Search/SearchModule';
import { Loading } from '@components/shared/Loading';
import { withAuth } from '@components/shared/withAuth';
import { getDeduplicatedSearchTerm } from '@helpers/getDeduplicatedSearchTerm';
import { isEmptyString } from '@helpers/utils';
import { _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output } from '@generated/grpc/spotify/s4mpview/v1';

const Container = styled.div`
  margin-bottom: calc(${spacer40} + ${spacer80});
`;

const SearchModuleContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(${cssColorVar('backgroundHighlight')});
  color: var(${cssColorVar('textBase')});
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: calc(${spacer40} + ${spacer20});
`;

export const UnauthedCatalog = () => {
  const { data: userInfo } = useCurrentUserInfo();
  const userFeeds:
    | _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[]
    | undefined = userInfo?.feeds;
  const userLicensors:
    | _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[]
    | undefined = userInfo?.licensors;

  const [inputValue, setInputValue] = useState<string>('');
  const [showPopover, setShowPopover] = useState(false);
  const t = useT();

  const [onboardingCookie, , deleteCookie] = useCookie('bulkSearchVisible');

  useEffect(() => {
    if (onboardingCookie) {
      setShowPopover(true);
      deleteCookie();
    }
  }, [deleteCookie, onboardingCookie, setShowPopover]);

  const {
    data: { releaseViewHistory, page, limit },
    actions: { onPagination, onLimitChange },
  } = useReleaseViewHistory(userFeeds, userLicensors);

  const {
    actions: { onSearchSubmit },
  } = useSearchedAlbums(userFeeds, userLicensors);

  const handleSearchChange = (value: string) => {
    setInputValue(value);
  };

  const handleSearchSubmit = () => {
    if (isEmptyString(inputValue.trim())) {
      return;
    }
    const deduplicatedInputValue = getDeduplicatedSearchTerm(inputValue);

    onSearchSubmit(deduplicatedInputValue);
  };

  if (!userFeeds) {
    return <Loading data-screen="catalog" />;
  }

  return (
    <Container>
      <SearchModuleContainer>
        <GridLayout>
          <SearchModule
            handleSearchChange={handleSearchChange}
            handleSubmit={handleSearchSubmit}
            inputValue={inputValue}
            totalResults={0}
            lastSearchedTerm=""
            popOver={
              showPopover && (
                <Popover
                  arrow="topStart"
                  popoverTitle={t(
                    'atlas.search.bulk_search_popover_title',
                    'Bulk Search',
                    'The term "Bulk Search" is a noun, not a verb. Serves as the title for a popover which explains how to bulk search for first time users',
                  )}
                  onClose={() => setShowPopover(false)}
                  colorSet="mutedAccent"
                  css={`
                    position: absolute;
                    margin-block-start: -${spacer8};
                    z-index: 1000;
                  `}
                >
                  {t(
                    'atlas.search.bulk_search_popover_content',
                    "You can now search by multiple product IDs at once by separating them with a comma. Please note that this is only available for product IDs. You can't mix format types such as names or titles.",
                    'Content for popover which explains how to bulk search for first time users',
                  )}
                </Popover>
              )
            }
            autoComplete={showPopover ? 'off' : 'on'}
          />
        </GridLayout>
      </SearchModuleContainer>

      <ResultsContainer>
        <GridLayout>
          <ContentArea
            releasesToShow={releaseViewHistory}
            userFeeds={userFeeds}
            userLicensors={userLicensors || []}
            paginationProps={{
              page,
              limit,
              handlePagination: onPagination,
              handleLimitChange: onLimitChange,
            }}
          />
        </GridLayout>
      </ResultsContainer>
    </Container>
  );
};

export const Catalog = withAuth(UnauthedCatalog);
